/*******************************************************************************
 * Copyright     : Copyright (c)  2026 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for any purpose without the express written permission of Honeywell. *
 *******************************************************************************/

import {
  IAvailableSoftwareVersion,
  IAvailableSoftwareVersionsApiResponse,
  IBaseTarVersion,
  IIupTarVersion,
  ICompatibleVersion,
  IConnectedDeviceSoftwareVersionResponse,
} from "../models/softwaredetails";
import { formatFileSize } from "./utility";

/**
 * Safely checks if a value is null or undefined
 */
const isNullOrUndefined = (value: any): boolean => {
  return value === null || value === undefined;
};

const getVersionFromUnknown = (value: any): string => {
  if (typeof value === "string") {
    return value;
  }

  if (!isNullOrUndefined(value) && typeof value === "object") {
    return String(value.version || value.value || value.text || "");
  }

  return "";
};

const getReleaseTypeFromUnknown = (value: any): string => {
  if (!isNullOrUndefined(value) && typeof value === "object") {
    return String(value.releaseType || "");
  }

  return "";
};

/**
 * Converts a number to a formatted file size string
 * @param size - The size in bytes (can be number, string, null, or undefined)
 * @returns Formatted file size string or "0" if not present
 */
const safeFormatFileSize = (size?: number | string | null): string => {
  if (isNullOrUndefined(size)) {
    return "0 B";
  }
  
  const sizeStr = typeof size === "number" ? size.toString() : String(size);
  return formatFileSize(sizeStr);
};

/**
 * Maps baseTarVersions to IAvailableSoftwareVersion[]
 */
const mapBaseTarVersions = (
  baseTarVersions: IBaseTarVersion[]
): IAvailableSoftwareVersion[] => {
  if (!Array.isArray(baseTarVersions)) {
    return [];
  }

  return baseTarVersions
    .filter((baseTarVersion): baseTarVersion is IBaseTarVersion => {
      return (
        !isNullOrUndefined(baseTarVersion) &&
        typeof baseTarVersion === "object" &&
        !isNullOrUndefined(baseTarVersion.version) &&
        baseTarVersion.version !== ""
      );
    })
    .map((baseTarVersion: IBaseTarVersion, index: number) => {
      const installedVersion = {
        version: String(baseTarVersion.version || ""),
        releaseType: String(baseTarVersion.releaseType || ""),
      };

      // Check both compatibleFullIupVersions and compatibleIupVersions
      const fullIupVersions = Array.isArray(baseTarVersion.compatibleFullIupVersions)
        ? baseTarVersion.compatibleFullIupVersions
        : [];
      const iupVersions = Array.isArray(baseTarVersion.compatibleIupVersions)
        ? baseTarVersion.compatibleIupVersions
        : [];

      // Combine both arrays
      const allCompatibleVersions = [...fullIupVersions, ...iupVersions];

      const availableVersions = allCompatibleVersions
        .filter((version): version is ICompatibleVersion => {
          return (
            !isNullOrUndefined(version) &&
            typeof version === "object" &&
            !isNullOrUndefined(version.version) &&
            version.version !== ""
          );
        })
        .map((version) => ({
          version: String(version.version || ""),
          size: safeFormatFileSize(version.size),
          releaseType: String(version.releaseType || ""),
        }));

      return {
        key: `base-${baseTarVersion.version}-${index}`,
        installedVersion,
        availableVersions,
      };
    });
};

/**
 * Maps iupTarVersions to IAvailableSoftwareVersion[]
 */
const mapIupTarVersions = (
  iupTarVersions: IIupTarVersion[]
): IAvailableSoftwareVersion[] => {
  if (!Array.isArray(iupTarVersions)) {
    return [];
  }

  return iupTarVersions
    .filter((iupTarVersion): iupTarVersion is IIupTarVersion => {
      return (
        !isNullOrUndefined(iupTarVersion) &&
        typeof iupTarVersion === "object" &&
        !isNullOrUndefined(iupTarVersion.version) &&
        iupTarVersion.version !== ""
      );
    })
    .map((iupTarVersion: IIupTarVersion, index: number) => {
      const installedVersion = {
        version: String(iupTarVersion.version || ""),
        releaseType: String(iupTarVersion.releaseType || ""),
      };

      const compatibleIupVersions = Array.isArray(
        iupTarVersion.compatibleIupVersions
      )
        ? iupTarVersion.compatibleIupVersions
        : [];

      const availableVersions = compatibleIupVersions
        .filter((version): version is ICompatibleVersion => {
          return (
            !isNullOrUndefined(version) &&
            typeof version === "object" &&
            !isNullOrUndefined(version.version) &&
            version.version !== ""
          );
        })
        .map((version) => ({
          version: String(version.version || ""),
          size: safeFormatFileSize(version.size),
          releaseType: String(version.releaseType || ""),
        }));

      return {
        key: `iup-${iupTarVersion.version}-${index}`,
        installedVersion,
        availableVersions,
      };
    });
};

/**
 * Maps the API response to IAvailableSoftwareVersion[] model for disconnected devices
 * Handles both baseTarVersions and iupTarVersions with dynamic response structure
 * 
 * Note: This mapper is specifically for disconnected device creation flow.
 * Connected devices may require different mapping logic.
 * 
 * Supports three response scenarios:
 * 1. Response with both baseTarVersions and iupTarVersions (both with data or empty)
 * 2. Response with only baseTarVersions (with compatibleFullIupVersions or compatibleIupVersions)
 * 3. Response with empty arrays for both
 * 
 * @param apiResponse - The API response containing baseTarVersions and/or iupTarVersions
 * @returns Array of IAvailableSoftwareVersion objects (empty array if no valid data)
 * 
 * @example
 * // Scenario 1: Both arrays with data
 * const response1 = { baseTarVersions: [...], iupTarVersions: [...] };
 * 
 * // Scenario 2: Only baseTarVersions
 * const response2 = { baseTarVersions: [...] };
 * 
 * // Scenario 3: Empty arrays
 * const response3 = { baseTarVersions: [], iupTarVersions: [] };
 */
export const mapDisconnectedDeviceSoftwareVersions = (
  apiResponse?: IAvailableSoftwareVersionsApiResponse | null
): IAvailableSoftwareVersion[] => {
  // Return empty array if no response or invalid response
  if (isNullOrUndefined(apiResponse)) {
    return [];
  }

  // Map baseTarVersions if present (handles both empty and populated arrays)
  const mappedBaseTarVersions = apiResponse.baseTarVersions
    ? mapBaseTarVersions(apiResponse.baseTarVersions)
    : [];

  // Map iupTarVersions if present (handles both empty and populated arrays)
  const mappedIupTarVersions = apiResponse.iupTarVersions
    ? mapIupTarVersions(apiResponse.iupTarVersions)
    : [];

  // Combine both arrays (will be empty if both sources are empty)
  return [...mappedBaseTarVersions, ...mappedIupTarVersions];
};

/**
 * Maps the connected device software version API response to IAvailableSoftwareVersion[]
 * Handles multiple response variations:
 * 1. Response with availableIupTarVersions
 * 2. Response with availableIupVersions (empty array)
 * 3. Response with availableVersions (empty array)
 * 4. Response with empty strings for installedVersion/releaseType
 * 
 * @param response - The API response from connected device
 * @returns Array of IAvailableSoftwareVersion objects
 */
export const mapConnectedDeviceSoftwareVersion = (
  response?: IConnectedDeviceSoftwareVersionResponse | null
): IAvailableSoftwareVersion[] => {
  // Handle null/undefined response
  if (isNullOrUndefined(response)) {
    return [];
  }

  // Get the available versions array from any of the possible property names
  const availableVersionsArray =
    response.availableIupTarVersions ||
    response.availableIupVersions ||
    response.availableVersions ||
    [];

  const installedVersionValue = getVersionFromUnknown(response.installedVersion);
  const installedReleaseType =
    getVersionFromUnknown(response.releaseType) ||
    getReleaseTypeFromUnknown(response.installedVersion);

  return [{
    key: `connected-${installedVersionValue || "unknown"}`,
    installedVersion: {
      version: installedVersionValue,
      releaseType: installedReleaseType,
    },
    availableVersions: Array.isArray(availableVersionsArray)
      ? availableVersionsArray
          .filter((version) => {
            return (
              !isNullOrUndefined(version) &&
              typeof version === "object" &&
              !isNullOrUndefined(version.version) &&
              version.version !== ""
            );
          })
          .map((version) => ({
            version: String(version.version || ""),
            size: safeFormatFileSize(version.size),
            releaseType: String(version.releaseType || ""),
          }))
      : [],
  }];
};
